// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import { BootstrapVue, IconsPlugin } from 'bootstrap-vue'
import PortalVue from 'portal-vue'
import App from './App'
import router from './router'
import store from './store'
import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'
import VueNumeric from 'vue-numeric'
import JsonExcel from 'vue-json-excel'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import BaseLoader from '@/app/shared/components/BaseLoader'
import numeral from './plugin/CustomNumeral'
import 'viewerjs/dist/viewer.css'
import Viewer from 'v-viewer'
import lodash from 'lodash'
import Loading from 'vue-loading-overlay'
import '@/utils/Filters'
import makeServer from './app/shared/plugins/Mirage'
import VueCompositionAPI, { provide } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import DateRangePicker from 'vue2-daterange-picker'
import moment from 'moment';
import VueMoment from 'vue-moment';
import VueGtag from 'vue-gtag';
import * as VueGoogleMaps from 'vue2-google-maps'

// Import Bootstrap an BootstrapVue CSS files (order is important)
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'


if (process.env.NODE_ENV === 'mirage') {
  makeServer({ environment: 'development' })
}

// Load Locales ('en' comes loaded by default)
require('moment/locale/es');

// Choose Locale
moment.locale('es');

Vue.use(VueCompositionAPI)
Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.GOOGLE_MAPS_API_KEY,
    libraries: 'visualization'
  }
})
Vue.use(BootstrapVue)
Vue.use(IconsPlugin)
Vue.use(PortalVue)
Vue.use(VueNumeric)
Vue.use(Viewer)
Vue.use(VueApexCharts)
Vue.use(DateRangePicker)

Vue.component('downloadExcel', JsonExcel)
Vue.component('pulseLoader', PulseLoader)
Vue.component('ExpertoRetailLoader', BaseLoader)
Vue.component('Loading', Loading)
Vue.component('apexchart', VueApexCharts)
Vue.component('DateRangePicker', DateRangePicker)
Vue.use(VueMoment, { moment });

Vue.use(VueGtag, {
  config: {
    id: 'G-FTH8CW3WQB',
    params: {
      send_page_view: false
    }
  }
});

// Inicializar Sentry
Sentry.init({
  Vue,
  dsn: 'https://fb3469f6478548e298d9976d235dd89f@o1332730.ingest.us.sentry.io/4510771407093760',
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', /^\//]
    })
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Captura 100% de las transacciones para monitoreo de performance
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  // Capturar errores no manejados automáticamente
  beforeSend (event, hint) {
    // Log para debugging (solo en desarrollo)
    if (process.env.NODE_ENV !== 'production') {
      console.log('📤 Enviando evento a Sentry:', event)
    }
    return event
  }
});

// Handler global para errores no capturados
if (typeof window !== 'undefined') {
  window.addEventListener('error', function (event) {
    Sentry.captureException(event.error || new Error(event.message))
  })

  window.addEventListener('unhandledrejection', function (event) {
    Sentry.captureException(event.reason)
  })
}

// Función de prueba para Sentry - disponible en consola del navegador
// Ejecuta: window.testSentryError() o window.testSentryMessage()
if (typeof window !== 'undefined') {
  window.testSentryError = function () {
    try {
      // Genera un error de prueba para verificar que Sentry lo capture
      throw new Error('Test error para verificar integración de Sentry')
    } catch (error) {
      if (typeof Sentry !== 'undefined' && Sentry.captureException) {
        const eventId = Sentry.captureException(error)
        console.log('✅ Error de prueba capturado y enviado a Sentry')
        console.log('📤 Event ID:', eventId)
      } else {
        console.error('❌ Sentry no está disponible')
      }
    }
  }

  window.testSentryMessage = function () {
    if (typeof Sentry !== 'undefined' && Sentry.captureMessage) {
      const eventId = Sentry.captureMessage('Test message para verificar integración de Sentry', 'info')
      console.log('✅ Mensaje de prueba enviado a Sentry')
      console.log('📤 Event ID:', eventId)
    } else {
      console.error('❌ Sentry no está disponible')
    }
  }

  window.testSentryStatus = function () {
    console.log('🔍 Verificando estado de Sentry...')
    console.log('Sentry disponible:', typeof Sentry !== 'undefined')
    if (typeof Sentry !== 'undefined') {
      console.log('Sentry.captureException:', typeof Sentry.captureException)
      console.log('Sentry.captureMessage:', typeof Sentry.captureMessage)
      console.log('Sentry.getCurrentHub:', typeof Sentry.getCurrentHub)
      try {
        const hub = Sentry.getCurrentHub()
        const client = hub.getClient()
        if (client) {
          console.log('✅ Sentry está inicializado correctamente')
          console.log('DSN:', client.getDsn ? client.getDsn() : 'N/A')
        } else {
          console.error('❌ Sentry client no está disponible')
        }
      } catch (e) {
        console.error('❌ Error al verificar Sentry:', e)
      }
    }
  }

  window.testSentryUndefinedFunction = function () {
    try {
      // Prueba con función indefinida (como solicitaste)
      myUndefinedFunction()
    } catch (error) {
      // Verificar que Sentry esté disponible
      if (typeof Sentry !== 'undefined' && Sentry.captureException) {
        // Capturar explícitamente el error en Sentry
        const eventId = Sentry.captureException(error)
        console.log('✅ Error de función indefinida capturado y enviado a Sentry')
        console.log('📤 Event ID:', eventId)
        console.log('🔍 Error details:', error)
      } else {
        console.error('❌ Sentry no está disponible o no está inicializado correctamente')
      }
      // NO re-lanzar el error para evitar "Uncaught" en consola
      // El error ya fue capturado y enviado a Sentry
    }
  }
}

Vue.mixin({
  methods: {
    deepCopy: function (obj) {
      var clone = {}
      for (var i in obj) {
        if (obj[i] != null && typeof (obj[i]) === 'object') {
          clone[i] = this.deepCopy(obj[i])
        } else {
          clone[i] = obj[i]
        }
      }
      return clone
    },
    deepCopyJsonMode: function (obj) {
      return JSON.parse(JSON.stringify(obj))
    },
    $_moneyNumeralFormat: value => {
      return numeral(value).format('$ 0.00 a')
    },
    $_moneyNumeralFormatVerbose: value => {
      return numeral(value).format('$ 0,0')
    },
    $_numberNumeralFormat: value => {
      return numeral(value).format('0.00 a')
    },
    $_numberNumeralFormatVerbose: value => {
      return numeral(value).format('0,0')
    },
    $_numberNumeralFormat_2: value => {
      return numeral(value).format('0,0')
    },
    $_percentageNumeralFormat: value => {
      return numeral(value).format('0%')
    },
    $_formatMetricValue (formatType, value) {
      if (formatType === 'PESOS') {
        return this.$_moneyNumeralFormat(value)
      } else if (formatType === 'UNITS') {
        return this.$_numberNumeralFormat(value)
      } else if (formatType === 'PERCENTAGE') {
        return this.$_percentageNumeralFormat(value)
      }
    },
    $_formatMetricValueVerbose (formatType, value) {
      if (formatType === 'PESOS') {
        return this.$_moneyNumeralFormatVerbose(value)
      } else if (formatType === 'UNITS') {
        return this.$_numberNumeralFormatVerbose(value)
      } else if (formatType === 'PERCENTAGE') {
        return this.$_percentageNumeralFormat(value)
      }
    }
  }
})

/* eslint-disable no-new */

Vue.prototype.$eventHub = new Vue()
Object.defineProperty(Vue.prototype, '$lodash', { value: lodash })

/* eslint-disable no-new */
new Vue({
  el: '#app',
  setup() {
    provide('vuex-store', store);
  },
  router,
  store,
  template: '<App/>',
  components: {
    App
  }
})



// WEBPACK FOOTER //
// ./src/main.js